// Header component - Memoized to prevent re-renders on route changes
import { memo, useMemo, useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, Search, AlertCircle, AlertTriangle } from 'lucide-react';
import { SearchBar } from '@components/ui/SearchBar';
import { ProfileDropdown } from './ProfileDropdown';
import { useSearch } from '@hooks';
import { useSidebar } from './SidebarContext';
import { useStableAuthValues } from './StableAuthValues';
import { useStableUser } from '@hooks/useStableUser';
import { UserPlan } from '@types';
import { useSuspensionStatus } from '@hooks/useSuspensionStatus';
import { clearSuspensionBannerDismissal } from '@components/SuspensionBanner';
import { clearOverflowBannerDismissal } from '@components/OverflowBanner';
import { reopenDowngradeWarningDialog } from '@components/modals/DowngradeWarningDialog';
import { reopenSuspensionWarningDialog } from '@components/modals/SuspensionWarningDialog';
import { supabase } from '@config/supabase';

const HeaderComponent: React.FC = () => {
  const { searchQuery, handleSearch, handleClear, isSearching } = useSearch({
    autoSearch: true,
  });
  const { toggleSidebar } = useSidebar();
  // Use stable auth values that only change when values actually change
  // This prevents re-renders when store updates but values remain the same
  const { user, isLoading, isAuthenticated, planReady } = useStableAuthValues();
  const { status: suspensionStatus, isLoading: suspensionLoading } = useSuspensionStatus();
  const [suspensionBannerDismissed, setSuspensionBannerDismissed] = useState(false);
  const [overflowBannerDismissed, setOverflowBannerDismissed] = useState(false);
  const [overflowStatus, setOverflowStatus] = useState<{ hasOverflow: boolean; isLoading: boolean }>({ hasOverflow: false, isLoading: true });
  const [shouldShowDowngradeDialogButton, setShouldShowDowngradeDialogButton] = useState(false);
  const [shouldShowSuspensionDialogButton, setShouldShowSuspensionDialogButton] = useState(false);

  // Check if suspension banner should be shown
  // Since banner is always visible on page load, button should only show if banner is dismissed in current session
  const shouldShowSuspensionButton = useMemo(() => {
    // Don't show if still loading
    if (isLoading || suspensionLoading) return false;
    
    // Don't show if not authenticated
    if (!isAuthenticated) return false;
    
    // Don't show if suspension status is not loaded yet
    if (!suspensionStatus) return false;
    
    // Only show if user is suspended with download access
    if (!suspensionStatus.isSuspended || !suspensionStatus.downloadEnabled) return false;
    
    // Only show button if banner is dismissed in current session
    return suspensionBannerDismissed;
  }, [isAuthenticated, isLoading, suspensionStatus, suspensionLoading, suspensionBannerDismissed]);

  const stableUser = useStableUser();

  // Check overflow status
  useEffect(() => {
    if (!isAuthenticated || !stableUser?.id) {
      setOverflowStatus({ hasOverflow: false, isLoading: false });
      return;
    }

    const checkOverflow = async () => {
      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('storage_used, storage_limit, overflow_state, downgrade_warning_started_at, is_suspended')
          .eq('id', stableUser.id)
          .single();

        if (!profile) {
          setOverflowStatus({ hasOverflow: false, isLoading: false });
          return;
        }

        const storageUsed = profile.storage_used || 0;
        const storageLimit = profile.storage_limit || 0;
        const isActuallyOverflow = storageUsed > storageLimit;
        const hasOverflowState = profile.overflow_state && profile.overflow_state !== 'none';
        const hasDowngradeWarning = !!profile.downgrade_warning_started_at;

        setOverflowStatus({
          hasOverflow: isActuallyOverflow && (hasOverflowState || hasDowngradeWarning) && !profile.is_suspended,
          isLoading: false,
        });
      } catch (error) {
        console.error('Error checking overflow:', error);
        setOverflowStatus({ hasOverflow: false, isLoading: false });
      }
    };

    checkOverflow();

    const channel = supabase
      .channel('header-overflow-check')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${stableUser.id}`,
        },
        () => {
          checkOverflow();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isAuthenticated, stableUser?.id]);

  // Check if downgrade warning dialog should be shown
  useEffect(() => {
    if (!isAuthenticated || !stableUser?.id) {
      setShouldShowDowngradeDialogButton(false);
      return;
    }

    const checkDowngradeWarning = async () => {
      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('downgrade_warning_started_at, downgrade_warning_dismissed, is_suspended, storage_used, storage_limit')
          .eq('id', stableUser.id)
          .single();

        if (!profile) {
          setShouldShowDowngradeDialogButton(false);
          return;
        }

        const hasWarning = !!profile.downgrade_warning_started_at;
        const isDismissed = profile.downgrade_warning_dismissed === true;
        const isSuspended = profile.is_suspended === true;
        const isOverflow = (profile.storage_used || 0) > (profile.storage_limit || 0);

        setShouldShowDowngradeDialogButton(hasWarning && !isDismissed && !isSuspended && isOverflow);
      } catch (error) {
        console.error('Error checking downgrade warning:', error);
        setShouldShowDowngradeDialogButton(false);
      }
    };

    checkDowngradeWarning();

    const channel = supabase
      .channel(`header-downgrade-check-${stableUser.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${stableUser.id}`,
        },
        () => {
          checkDowngradeWarning();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isAuthenticated, stableUser?.id]);

  // Check if suspension warning dialog should be shown
  useEffect(() => {
    if (!isAuthenticated || !stableUser?.id || !stableUser?.isSuspended) {
      setShouldShowSuspensionDialogButton(false);
      return;
    }

    const checkSuspensionWarning = async () => {
      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('suspension_auto_delete, suspension_download_enabled, suspension_download_expires_at')
          .eq('id', stableUser.id)
          .single();

        if (!profile) {
          setShouldShowSuspensionDialogButton(false);
          return;
        }

        const shouldShow = 
          profile.suspension_auto_delete === true &&
          profile.suspension_download_enabled === true &&
          profile.suspension_download_expires_at !== null;

        setShouldShowSuspensionDialogButton(shouldShow);
      } catch (error) {
        console.error('Error checking suspension warning:', error);
        setShouldShowSuspensionDialogButton(false);
      }
    };

    checkSuspensionWarning();

    const channel = supabase
      .channel(`header-suspension-check-${stableUser.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${stableUser.id}`,
        },
        () => {
          checkSuspensionWarning();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isAuthenticated, stableUser?.id, stableUser?.isSuspended]);

  // Listen for custom reopen events to update state
  useEffect(() => {
    const handleSuspensionReopen = () => {
      setSuspensionBannerDismissed(false);
    };

    const handleOverflowReopen = () => {
      setOverflowBannerDismissed(false);
    };

    window.addEventListener('suspension-banner-reopen', handleSuspensionReopen);
    window.addEventListener('overflow-banner-reopen', handleOverflowReopen);
    return () => {
      window.removeEventListener('suspension-banner-reopen', handleSuspensionReopen);
      window.removeEventListener('overflow-banner-reopen', handleOverflowReopen);
    };
  }, []);

  const handleReopenSuspensionBanner = () => {
    clearSuspensionBannerDismissal();
    setSuspensionBannerDismissed(false);
  };

  const handleReopenOverflowBanner = () => {
    clearOverflowBannerDismissal();
    setOverflowBannerDismissed(false);
  };

  // Check if overflow banner button should be shown
  const shouldShowOverflowButton = useMemo(() => {
    if (overflowStatus.isLoading || isLoading) return false;
    if (!isAuthenticated) return false;
    if (!overflowStatus.hasOverflow) return false;
    // Only show button if banner is dismissed in current session
    return overflowBannerDismissed;
  }, [isAuthenticated, isLoading, overflowStatus, overflowBannerDismissed]);
  
  // CRITICAL: Stabilize isLoading and isAuthenticated to prevent flicker on tab switch
  // Once loading is complete, keep it false even if it briefly becomes true
  const stableIsLoading = useRef(false);
  const stableIsAuthenticated = useRef(false);
  
  useEffect(() => {
    if (!isLoading) {
      stableIsLoading.current = false;
    } else if (stableUser) {
      // If we have a stable user, don't show loading state
      stableIsLoading.current = false;
    }
  }, [isLoading, stableUser]);
  
  useEffect(() => {
    if (isAuthenticated && stableUser) {
      stableIsAuthenticated.current = true;
    } else if (!user && !stableUser) {
      // Only reset if we really don't have a user
      stableIsAuthenticated.current = false;
    } else if (stableUser) {
      // If we have a stable user, keep authenticated true
      stableIsAuthenticated.current = true;
    }
  }, [isAuthenticated, user, stableUser]);
  
  // CRITICAL: If we have a stable user, never show loading or unauthenticated state
  const isActuallyLoading = stableUser ? false : (isLoading && stableIsLoading.current === false ? false : isLoading);
  const isActuallyAuthenticated = stableUser ? true : (stableIsAuthenticated.current || isAuthenticated);
  
  // Hide upgrade button if user has at least Bedroom Plan
  // Only show upgrade button if:
  // 1. Auth is fully loaded (not loading)
  // 2. User is authenticated
  // 3. User data exists and plan is explicitly set and is FREE
  // CRITICAL: Use stableUser and stable states to prevent flicker on tab switch
  // planReady: Plan-UI erst nach ~180ms anzeigen → kein FREE-Flackern
  const shouldShowUpgrade = useMemo(() => {
    if (!planReady) return false;
    if (isActuallyLoading) return false;
    if (!isActuallyAuthenticated) return false;
    if (!stableUser) return false;
    if (!stableUser.id) return false;
    return stableUser.plan === UserPlan.FREE;
  }, [planReady, isActuallyLoading, isActuallyAuthenticated, stableUser?.id, stableUser?.plan]);

  return (
    <header className="fixed top-0 left-0 right-0 h-[50px] sm:h-[55px] md:h-[60px] lg:h-[65px] xl:h-[70px] flex items-center justify-between px-2 xs:px-2.5 sm:px-3 md:px-4 lg:px-12 xl:px-16 2xl:px-20 z-50 bg-gradient-to-b from-dark-secondary/95 via-dark-secondary/90 to-dark-secondary/85 backdrop-blur-xl border-b border-white/[0.08] shadow-[0_1px_0_0_rgba(255,255,255,0.03)_inset] sidebar-offset-header">
      {/* Burger Menu Button - ALWAYS visible below 768px, positioned ABSOLUTELY on very small screens */}
      <button
        onClick={toggleSidebar}
        className="md:hidden absolute left-2 xs:left-2.5 sm:relative sm:left-auto flex-shrink-0 w-11 h-11 xs:w-12 xs:h-12 sm:w-10 sm:h-10 flex items-center justify-center bg-white/[0.03] hover:bg-white/[0.08] active:bg-white/[0.12] rounded-lg text-white/90 hover:text-white border border-white/[0.06] hover:border-white/[0.12] transition-all touch-manipulation z-[100] backdrop-blur-sm"
        aria-label="Toggle menu"
        style={{ minWidth: '44px', minHeight: '44px' }}
      >
        <Menu className="w-6 h-6 xs:w-6 xs:h-6 sm:w-5 sm:h-5" strokeWidth={2.5} />
      </button>
      
      {/* Left Section: Search Bar - with margin for burger button on mobile */}
      <div className="flex items-center gap-1.5 xs:gap-2 sm:gap-2 md:gap-0 min-w-0 flex-1 md:flex-none ml-12 xs:ml-14 sm:ml-0">
        {/* Search Bar - Hidden on very small screens (340px), visible from xs (360px) */}
        <div className="hidden xs:block flex-1 min-w-0 md:flex-none md:w-[300px] lg:w-[320px] xl:w-[350px] 2xl:w-[380px] mr-1 xs:mr-1.5 sm:mr-2">
          <SearchBar 
            placeholder="Search..."
            onSearch={handleSearch}
            onClear={handleClear}
            value={searchQuery}
            isSearching={isSearching}
            className="w-full"
          />
        </div>
      </div>
      
      {/* Search Icon Button - Show on very small screens (340px) instead of full search bar */}
      <div className="flex xs:hidden items-center justify-center flex-shrink-0 mr-2">
        <button
          onClick={() => {
            // Mobile search - could open a modal or focus the search bar
            const searchInput = document.querySelector('input[type="text"][placeholder*="Search"]') as HTMLInputElement;
            if (searchInput) {
              searchInput.focus();
            }
          }}
          className="w-10 h-10 flex items-center justify-center bg-white/[0.05] hover:bg-white/[0.1] rounded-lg text-white/80 hover:text-white border border-white/[0.08] hover:border-white/[0.15] transition-all touch-manipulation min-w-[40px] min-h-[40px] backdrop-blur-sm"
          aria-label="Search"
        >
          <Search className="w-5 h-5" strokeWidth={2} />
        </button>
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-1 xs:gap-1.5 sm:gap-2 md:gap-3 flex-shrink-0">
        {/* Overflow Banner Reopen Button */}
        {shouldShowOverflowButton && (
          <button
            onClick={handleReopenOverflowBanner}
            className="flex items-center gap-1.5 px-2.5 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg transition-all border border-red-500/30 hover:border-red-500/50"
            aria-label="Show storage overflow information"
            title="Show storage overflow information"
          >
            <AlertTriangle className="w-4 h-4" />
            <span className="hidden sm:inline text-xs font-medium">Overflow</span>
          </button>
        )}

        {/* Suspension Banner Reopen Button */}
        {shouldShowSuspensionButton && (
          <button
            onClick={handleReopenSuspensionBanner}
            className="flex items-center gap-1.5 px-2.5 py-1.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 rounded-lg transition-all border border-amber-500/30 hover:border-amber-500/50"
            aria-label="Show suspension information"
            title="Show suspension information"
          >
            <AlertCircle className="w-4 h-4" />
            <span className="hidden sm:inline text-xs font-medium">Suspended</span>
          </button>
        )}

        {/* Downgrade Warning Dialog Reopen Button */}
        {shouldShowDowngradeDialogButton && (
          <button
            onClick={() => reopenDowngradeWarningDialog()}
            className="flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 rounded-lg transition-all border border-blue-500/30 hover:border-blue-500/50"
            aria-label="Show downgrade warning"
            title="Show downgrade warning"
          >
            <AlertTriangle className="w-4 h-4" />
            <span className="hidden sm:inline text-xs font-medium">Downgrade Warning</span>
          </button>
        )}

        {/* Suspension Warning Dialog Reopen Button */}
        {shouldShowSuspensionDialogButton && (
          <button
            onClick={() => reopenSuspensionWarningDialog()}
            className="flex items-center gap-1.5 px-2.5 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg transition-all border border-red-500/30 hover:border-red-500/50"
            aria-label="Show suspension warning"
            title="Show suspension warning"
          >
            <AlertTriangle className="w-4 h-4" />
            <span className="hidden sm:inline text-xs font-medium">Deletion Warning</span>
          </button>
        )}

        {/* Upgrade Button - Hidden on mobile and if user has at least Bedroom Plan */}
        {/* Only render if user data is fully loaded to prevent flash */}
        {!isActuallyLoading && isActuallyAuthenticated && stableUser && (
          <div className={`hidden sm:block transition-opacity duration-200 ${
            shouldShowUpgrade ? 'opacity-100' : 'opacity-0 pointer-events-none w-0 overflow-hidden'
          }`}>
            <Link
              to="/pricing"
              className="flex items-center justify-center h-8 px-4 sm:px-5 bg-gradient-to-r from-[#30d870]/90 to-[#00ff61]/90 hover:from-[#30d870] hover:to-[#00ff61] rounded-full text-[13px] sm:text-sm font-semibold text-black/90 tracking-wide shadow-sm hover:shadow-md hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 border border-white/10"
            >
              Upgrade
            </Link>
          </div>
        )}

        {/* Profile Dropdown */}
        <ProfileDropdown />
      </div>
    </header>
  );
};

// Memoize Header to prevent re-renders on route changes
// The real optimization is in MainLayout using useMemo with stable keys
export const Header = memo(HeaderComponent);
Header.displayName = 'Header';